/**
 * React Query hooks for the ADR-141 notification tree.
 *
 * Keys are factory-built per alert-type id. The rules API is
 * append-only (POST for an existing target closes the old row and
 * returns a NEW id; DELETE closes) so every mutation invalidates the
 * type's key ROOT and consumers refetch — never patch a cached rule or
 * hold an id across a write (rubric discipline; see
 * `useRubricCells.ts` for the precedent).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContactRule,
  deleteContactRule,
  getContactRules,
  type AlertTypeContactRule,
  type AlertTypeContactRuleWritePayload,
} from '../../services/contactRules.service';

export const contactRuleKeys = {
  all: ['contactRules'] as const,
  type: (alertTypeId: number) =>
    [...contactRuleKeys.all, alertTypeId] as const,
  list: (alertTypeId: number) =>
    [...contactRuleKeys.type(alertTypeId), 'list'] as const,
};

export function useContactRules(alertTypeId: number | null | undefined) {
  return useQuery({
    queryKey: contactRuleKeys.list(alertTypeId ?? 0),
    queryFn: () => getContactRules(alertTypeId as number),
    enabled: !!alertTypeId,
  });
}

export function useCreateContactRule(alertTypeId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    AlertTypeContactRule,
    Error,
    AlertTypeContactRuleWritePayload
  >({
    mutationFn: (body) => createContactRule(alertTypeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contactRuleKeys.type(alertTypeId),
      });
    },
  });
}

export function useDeleteContactRule(alertTypeId: number) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (ruleId) => deleteContactRule(alertTypeId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contactRuleKeys.type(alertTypeId),
      });
    },
  });
}
